import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Users, 
  Settings, 
  RotateCw, 
  Plus, 
  Edit2, 
  Trash2, 
  Eye, 
  EyeOff,
  Server,
  Activity
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  created_at: string;
}

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });

  const queryClient = useQueryClient();

  // Users query
  const { data: users, isLoading: usersLoading } = useQuery(
    'admin-users',
    () => api.getUsers()
  );

  // Sync logs query
  const { data: syncLogs, isLoading: logsLoading } = useQuery(
    'sync-logs',
    () => api.getSyncLogs()
  );

  // Portals query
  const { data: portals, isLoading: portalsLoading } = useQuery(
    'admin-portals',
    () => api.getPortals()
  );

  // Create user mutation
  const createUserMutation = useMutation(
    (userData: any) => api.createUser(userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
        setShowUserModal(false);
        setUserForm({ username: '', email: '', password: '', role: 'user' });
        toast.success('Utente creato con successo');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Errore nella creazione utente');
      }
    }
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    ({ id, userData }: { id: string; userData: any }) => api.updateUser(id, userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
        setShowUserModal(false);
        setEditingUser(null);
        setUserForm({ username: '', email: '', password: '', role: 'user' });
        toast.success('Utente aggiornato con successo');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Errore nell\'aggiornamento utente');
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    (id: string) => api.deleteUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
        toast.success('Utente eliminato con successo');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Errore nell\'eliminazione utente');
      }
    }
  );

  // Full sync mutation
  const fullSyncMutation = useMutation(
    () => api.triggerFullSync(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sync-logs');
        toast.success('Sincronizzazione completa avviata');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Errore nell\'avvio sincronizzazione');
      }
    }
  );

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingUser) {
      updateUserMutation.mutate({ 
        id: editingUser.id, 
        userData: userForm 
      });
    } else {
      createUserMutation.mutate(userForm);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      deleteUserMutation.mutate(id);
    }
  };

  const resetUserForm = () => {
    setUserForm({ username: '', email: '', password: '', role: 'user' });
    setEditingUser(null);
    setShowUserModal(false);
  };

  const tabs = [
    { id: 'users', name: 'Utenti', icon: Users },
    { id: 'sync', name: 'Sincronizzazione', icon: RotateCw },
    { id: 'portals', name: 'Portali', icon: Server },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Amministrazione
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gestione utenti, sincronizzazione e configurazioni di sistema
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {Array.isArray(tabs) ? tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            );
          }) : null}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Gestione Utenti
            </h2>
            <button
              onClick={() => setShowUserModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Utente
            </button>
          </div>

          {usersLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {Array.isArray(users?.data) ? users.data.map((user: User) => (
                  <li key={user.id}>
                    <div className="px-4 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.username}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {user.role}
                        </span>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                )) : null}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Sync Tab */}
      {activeTab === 'sync' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Sincronizzazione Dati
            </h2>
            <button
              onClick={() => fullSyncMutation.mutate()}
              disabled={fullSyncMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              <RotateCw className={`h-4 w-4 mr-2 ${fullSyncMutation.isLoading ? 'animate-spin' : ''}`} />
              Sincronizzazione Completa
            </button>
          </div>

          {logsLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {Array.isArray(syncLogs?.data) ? syncLogs.data.map((log: any) => (
                  <li key={log.id}>
                    <div className="px-4 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Activity className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {log.portal_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(log.created_at).toLocaleString('it-IT')}
                            </div>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          log.status === 'success' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {log.status}
                        </span>
                      </div>
                      {log.message && (
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          {log.message}
                        </div>
                      )}
                    </div>
                  </li>
                )) : null}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Portals Tab */}
      {activeTab === 'portals' && (
        <div className="space-y-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Portali Configurati
          </h2>

          {portalsLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(portals?.data) ? portals.data.map((portal: any) => (
                <div key={portal.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {portal.nome}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      portal.attivo 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                      {portal.attivo ? 'Attivo' : 'Inattivo'}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <strong>Comune:</strong> {portal.comune}
                    </div>
                    <div>
                      <strong>Tipo:</strong> {portal.tipo}
                    </div>
                    <div>
                      <strong>URL:</strong> 
                      <a href={portal.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 ml-1">
                        {portal.url}
                      </a>
                    </div>
                    <div>
                      <strong>Ultimo sync:</strong> {portal.last_sync ? new Date(portal.last_sync).toLocaleString('it-IT') : 'Mai'}
                    </div>
                  </div>
                </div>
              )) : null}
            </div>
          )}
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={resetUserForm} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
              </h3>
              <form onSubmit={handleSubmitUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={userForm.username}
                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password {editingUser && '(lascia vuoto per non cambiare)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      required={!editingUser}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ruolo
                  </label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetUserForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={createUserMutation.isLoading || updateUserMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editingUser ? 'Aggiorna' : 'Crea'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;